import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Users, Shield, Trash2, UserCog } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Feil ved lasting av brukarar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRoleChange = async (email, newRole) => {
    try {
      await axios.put(
        `${API}/admin/users/${encodeURIComponent(email)}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      toast.success("Brukarrolle oppdatert");
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(error.response?.data?.detail || "Feil ved oppdatering av brukar");
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(userToDelete.email)}`, {
        withCredentials: true,
      });
      toast.success("Brukar sletta");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "Feil ved sletting av brukar");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB]">
        <div className="animate-pulse text-[#4B647F]">Lastar brukarar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F8FB]">
      {/* Header */}
      <header className="bg-white border-b border-[#E6E9F0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/admin")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Users className="w-6 h-6 text-[#103766]" />
                <h1 className="text-xl font-semibold text-[#1F2A36]">
                  Brukaradministrasjon
                </h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-white border border-[#E6E9F0] rounded-lg">
          <CardHeader className="border-b border-[#E6E9F0] pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
                <UserCog className="w-5 h-5 text-[#103766]" />
                Alle brukarar
              </CardTitle>
              <Badge variant="outline">{users.length} brukarar</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F6F8FB]">
                  <TableHead className="text-[#4B647F]">Brukar</TableHead>
                  <TableHead className="text-[#4B647F]">E-post</TableHead>
                  <TableHead className="text-[#4B647F]">Rolle</TableHead>
                  <TableHead className="text-[#4B647F]">Oppretta</TableHead>
                  <TableHead className="text-[#4B647F] text-right">Handlingar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.user_id} className="hover:bg-[#F6F8FB]/50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img
                            src={user.picture}
                            alt={user.name}
                            className="w-8 h-8 rounded-full"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#103766]/10 flex items-center justify-center">
                            <Users className="w-4 h-4 text-[#103766]" />
                          </div>
                        )}
                        <span className="font-medium text-[#1F2A36]">
                          {user.name}
                        </span>
                        {user.user_id === currentUser?.user_id && (
                          <Badge variant="secondary" className="text-xs">
                            Du
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-[#4B647F]">{user.email}</TableCell>
                    <TableCell>
                      <Select
                        value={user.role}
                        onValueChange={(value) => handleRoleChange(user.email, value)}
                        disabled={user.email === currentUser?.email || user.email === "paffen@gmail.com"}
                      >
                        <SelectTrigger
                          className="w-[120px] border-[#E6E9F0]"
                          data-testid={`role-select-${user.user_id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">
                            <div className="flex items-center gap-2">
                              <Users className="w-3 h-3" />
                              Les
                            </div>
                          </SelectItem>
                          <SelectItem value="editor">
                            <div className="flex items-center gap-2">
                              <UserCog className="w-3 h-3" />
                              Redaktør
                            </div>
                          </SelectItem>
                          <SelectItem value="admin">
                            <div className="flex items-center gap-2">
                              <Shield className="w-3 h-3" />
                              Admin
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-[#8AA4BF]">
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString("nb-NO")
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setUserToDelete(user);
                          setDeleteDialogOpen(true);
                        }}
                        disabled={user.email === currentUser?.email || user.email === "paffen@gmail.com"}
                        data-testid={`delete-user-${user.user_id}`}
                        className="text-[#D35E4E] hover:text-[#D35E4E] hover:bg-[#D35E4E]/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slett brukar</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil slette brukaren{" "}
              <strong>{userToDelete?.name}</strong>? Denne handlinga kan ikkje
              gjerast om.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-[#D35E4E] hover:bg-[#B84535]"
              data-testid="confirm-delete-btn"
            >
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagement;
